import React, { useEffect, useState } from 'react';
import { Avatar, Icon, IconButton, Snackbar, TablePagination, Tooltip } from '@mui/material';
import AnnotationForm from './AnnotationForm';
import { Annotation } from '../../../models/portfolio/annotation';
import { helperService } from '../../../services/helper.service';
import { portfolioService } from '../../../services/portfolio.service';
import ProgressBar from '../../controls/ProgressBar';
import ErrorMessage from '../../controls/ErrorMessage';
import { ListParams } from '../../../models/listParams';
import DialogTimeline from '../../controls/dialogs/DialogTimeline';

interface AnnotationRepliesProps {
  portfolioId?: string,
  object: any;
  type: 'annotation' | 'task';
  mijnOrganisatieId?: string;
  account: any;
  visible: boolean;
  organizations?: any[];
  readOnly?: boolean;
}

const AnnotationReplies: React.FC<AnnotationRepliesProps> = ({
  portfolioId,
  object,
  type,
  mijnOrganisatieId,
  account,
  visible,
  organizations,
  readOnly = false,
}) => {
  const [archivedAnnotations, setArchivedAnnotations] = useState<Annotation[]>([]);
  const [selectedAnnotation, setSelectedAnnotation] = useState<Annotation | undefined>(undefined);

  const [editingReplyId, setEditingReplyId] = useState<string | null>(null);
  const [replies, setReplies] = useState<Annotation[]>([]);
  const [replyCount, setReplyCount] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(5);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [open, setOpenDialogs] = useState({
    timeline: false,
    snackbar: false
  });

  useEffect(() => {
    if (!visible) return;
  
    const alreadyLoaded = replies.length > 0;
    if (!alreadyLoaded) {
      fetchReplies(0, rowsPerPage);
    }
  }, [visible, type, object]);
  
  const openDialog = (dialog: keyof typeof open) => {
    // setSelectedId(id);
    setOpenDialogs((prev) => ({ ...prev, [dialog]: true }));
  };

  const closeDialog = (dialog: keyof typeof open) => {
    setOpenDialogs((prev) => ({ ...prev, [dialog]: false }));
  };

  const fetchReplies = async (page: number, size: number) => {
    setLoading(true);
    setError(null);

    try {
      const params: ListParams = {
        offset: page * size,
        limit: size,
      };

      let result;
      if (type === 'annotation') {
        result = await portfolioService.getAnnotationChildren(object.id!, params);
      }
      else if (type === 'task') {
        result = await portfolioService.getAnnotationsByTask(object.id!, params);
      }

      if (result?.items) {
        setReplies(result.items);
        setReplyCount(result.count);
        setPage(page);
        setRowsPerPage(size);
      }
    }
    catch (err: any) {
      setError('Fout bij laden van opmerkingen');
    }
    finally {
      setLoading(false);
    }
  };

  const handleSubmitReply = async (reply: Annotation) => {
    if (readOnly) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let response;

      if (reply.id) {
        response = await portfolioService.putAnnotation(reply);
      } 
      else {
        response = await portfolioService.postAnnotation(reply);
      }


      if (response?.id) {
        setTimeout(async () => {
          setSnackbarMessage('Plan toegevoegd aan afstemmingsdossier');
          openDialog('snackbar');

          await fetchReplies(page, rowsPerPage);
          setEditingReplyId(null);
          setLoading(false);
        }, 3000);
      }
    } 
    catch (err) {
      setError('Fout bij opslaan van een opmerking');
      setLoading(false);
    } 
  };

  const onClickHistoryAnnotation = async (annotation: Annotation) => {
    if (annotation) {
      setSelectedAnnotation(annotation);
      await fetchAnnotationsArchived(annotation.id || '');
      openDialog('timeline');
    }
  };

  const fetchAnnotationsArchived = async (annotationId: string) => {
    try {
      if (annotationId) {
        setLoading(true);

        const data = await portfolioService.getAnnotationsArchived(annotationId) as any;
        if (data) {
          // console.log('Fetched archived annotations:', data);

          const sortedItems = [...data.items].sort((a, b) =>
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
          );

          setArchivedAnnotations(sortedItems);
        }

        setLoading(false);
      }
    }
    catch (error: any) {
      setError(error.message || 'Er is een fout opgetreden');
      setLoading(false);
    }
  };

  const getOrganisatieNaam = (organisatieId: string) => {
    const organisatie = organizations?.find(org => org.id === organisatieId);
    return organisatie?.name ?? organisatieId; // fallback to ID if name not found
  };

  if (!visible) return null;

  return (
    <>

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={open.snackbar}
        autoHideDuration={2000}
        onClose={() => closeDialog('snackbar')}
        message={snackbarMessage ? snackbarMessage : 'Er is een fout opgetreden'}
        key={'topcenter'}
      />

      <div className="annotation-children">
        {!readOnly && (
          <div className="annotation-card child">
            <Avatar
              sx={{
                width: 44,
                height: 44,
                padding: 0,
                bgcolor: helperService.getUserColor(helperService.getAvatarLetters(account?.name || '?')),
              }}
            >
              {helperService.getAvatarLetters(account?.name || '?')}
            </Avatar>
            <AnnotationForm
              parentId={object.id}
              onSubmit={(text) => {
                const newReply: Annotation = {
                  id: null,
                  description: text,
                  portfolioId: portfolioId,
                  ...(type === 'annotation' && { parentAnnotationId: object.id }),
                  ...(type === 'task' && { taskId: object.id }),
                };
                handleSubmitReply(newReply);
              }}
            />
          </div>
        )}

        {replies.map(reply => (
          <div key={reply.id} className="annotation-card child">
            <Avatar
              sx={{ width: 44, height: 44, padding: 0, bgcolor: helperService.getUserColor(reply.createdByUser || '?') }}
            >
              {reply.createdByUser?.charAt(0).toUpperCase()}
            </Avatar>

            {/* JSON: {JSON.stringify(reply, null, 2)} <br/> */}

            {editingReplyId === reply.id ? (
              <AnnotationForm
                parentId={object.id}
                initialValue={reply.description ?? ''}
                onSubmit={(text) => {
                    const updated = { 
                      ...reply,
                      description: text, 
                      portfolioId: portfolioId, 
                      ...(type === 'annotation' && { parentAnnotationId: object.id }), 
                      ...(type === 'task' && { taskId: object.id }) 
                    };
                  handleSubmitReply(updated);
                }}
              />
            ) : (
              <p>{reply.description}</p>
            )}

            <div className="annotation-footer">
              <div className="meta">
                {/* <Tooltip title={`Geplaatst namens: ${getOrganisatieNaam(reply.createdByOrganization ?? '')}`} arrow>
                  <div className="organization">{getOrganisatieNaam(reply.createdByOrganization ?? '')}</div>
                </Tooltip> */}
                <Tooltip
                  title={
                    <div>
                      Geplaatst door: {reply.createdByUser?.toLowerCase() ?? 'Onbekend gebruiker'}
                      <br />
                      namens {getOrganisatieNaam(reply.createdByOrganization ?? '')}
                    </div>
                  }
                  arrow
                >
                  <div className="user">{reply.createdByUser?.toLowerCase() ?? 'Onbekend gebruiker'}</div>
                </Tooltip>
                <Tooltip title={`Geplaatst op: ${helperService.formatDutchDate(reply.timestamp)}`} arrow>
                  <div className="date">{helperService.formatDutchDate(reply.timestamp)}</div>
                </Tooltip>
              </div>

              <div className="actions">
                {/* {reply.createdByOrganization === mijnOrganisatieId && ( <></> )} */}

                <Tooltip title="Bekijk historie" arrow>
                  <IconButton color="secondary" size="small" onClick={() => onClickHistoryAnnotation(reply)}>
                    <Icon>timeline</Icon>
                  </IconButton>
                </Tooltip>

                {!readOnly && reply.createdByOrganization === mijnOrganisatieId && (
                  editingReplyId === reply.id ? (
                    <Tooltip title="Annuleer wijzigen" arrow>
                      <IconButton color="secondary" size="small" onClick={() => setEditingReplyId(null)}>
                        <Icon>edit_off</Icon>
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Tooltip title="Wijzigen opmerking" arrow>
                      <IconButton color="secondary" size="small" onClick={() => setEditingReplyId(reply.id!)}>
                        <Icon>edit</Icon>
                      </IconButton>
                    </Tooltip>
                  )
                )}
              </div>
            </div>
          </div>
        ))}
        
      </div>

      {replyCount > 0 && (
        <div className="pagination-wrapper">
          <TablePagination
            component="div"
            count={replyCount}
            page={page}
            onPageChange={(_, newPage) => fetchReplies(newPage, rowsPerPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => fetchReplies(0, parseInt(e.target.value, 10))}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            labelRowsPerPage="Opmerkingen per pagina:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} van ${count}`}
          />
        </div>
      )}

      {archivedAnnotations.length > 0 && (
        <DialogTimeline
          open={open.timeline}
          items={archivedAnnotations as any[]}
          type="annotation"
          title={`Historie van deze opmerking: ${selectedAnnotation ? selectedAnnotation.title : ''}`}
          organizations={organizations}
          onClose={ () => closeDialog('timeline') }
        />
      )}

      { loading && (
        <ProgressBar />
      )}

      { error && (
        <ErrorMessage message={error} />
      )}
    </>
    
  );
};

export default AnnotationReplies;
